import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Dialog, IconButton, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import {
    createProjectFromEditor, deleteFacilityProjectAttachment, getAllClientsForFilter, getAllFacilities, getJoinedGreenUsers,
    getProjectEditor, getProjectTaskTags, saveProjectEditor, uploadFacilityProjectAttachment,
} from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import { emptyStep, emptySubTask, emptyTask, newKey, standardItems, validateDraft } from './editorOptions';
import ProjectDetailsSection from './ProjectDetailsSection';
import TimelineItemsList from './TimelineItemsList';
import TaskPanel from './TaskPanel';
import ClientPreview from './ClientPreview';
import BudgetVersionList from '../budget/BudgetVersionList';

const blankProject = (clientId = '') => ({ client_id: clientId, facility_id: '', description: '', project_owner: [], project_budget: '', project_type: '', contract_years: null });

// Server items → editor draft (every item and sub-task gets a stable key)
const toDraftItems = (items) => items.map(it => (it.kind === 'task'
    ? { ...it, key: newKey(), sub_tasks: (it.sub_tasks || []).map(s => ({ ...s, key: `s${s.id}`, attachments: s.attachments || [] })) }
    : { ...it, key: newKey() }));

// Draft → save payload (attachments are uploaded separately after the save)
const toPayloadItems = (items) => items.map(it => (it.kind === 'task'
    ? { kind: 'task', task_id: it.task_id, task_name: it.task_name.trim(), client_visible: it.client_visible,
        sub_tasks: it.sub_tasks.map(s => ({ id: s.id, key: s.key, sub_task_name: s.sub_task_name.trim(), start_date: s.start_date, end_date: s.end_date, description: s.description, spoc: s.spoc, status: s.status })) }
    : { kind: 'step', label: it.label.trim(), client_visible: it.client_visible, status: it.status, date_label: it.date_label, start_date: it.start_date, end_date: it.end_date }));

// The combined project editor: details, timeline, tasks and sub-tasks in one place. Saves to the same tables as the
// Settings › Project Management form (with the same rules), so both always show the same project.
export default function ProjectEditorDialog({ projectId, onClose, onSaved }) {
    const dispatch = useDispatch();
    const fullScreen = useMediaQuery('(max-width:900px)');
    const isNew = !projectId;
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(null); // { project, items, version, suggested }
    const [clients, setClients] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [greenUsers, setGreenUsers] = useState([]);
    const [taskTags, setTaskTags] = useState([]);
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState(null);
    const [conflict, setConflict] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [removedFiles, setRemovedFiles] = useState([]);
    const [panel, setPanel] = useState(null); // { taskKey, focusKey }

    const loadFacilities = useCallback((clientId) => {
        if (!clientId) { setFacilities([]); return; }
        dispatch(getAllFacilities(clientId, {})).then((res) => setFacilities(res?.data?.success ? res.data.data || [] : []));
    }, [dispatch]);

    const load = useCallback(() => {
        setLoading(true); setMessage(null); setConflict(false); setErrors({}); setRemovedFiles([]); setDirty(false);
        dispatch(getJoinedGreenUsers()).then((res) => {
            const users = res?.data?.success ? (res.data.allData || res.data.data || []) : [];
            setGreenUsers(users.filter(u => (u.user_id || '').toLowerCase().includes('@greenvironment')));
        });
        dispatch(getProjectTaskTags()).then((res) => setTaskTags(res?.data?.success ? res.data.data || [] : []));
        if (isNew) {
            dispatch(getAllClientsForFilter()).then((res) => setClients(res?.data?.success ? res.data.allClients || [] : []));
            setDraft({ project: blankProject(), items: standardItems(), version: null, suggested: false });
            setLoading(false);
            return;
        }
        dispatch(getProjectEditor(projectId)).then((res) => {
            if (res?.data?.success) {
                const d = res.data.data;
                setDraft({ project: { ...d.project, project_budget: d.project.project_budget ?? '' }, items: toDraftItems(d.items), version: d.version, suggested: d.suggested });
                loadFacilities(d.project.client_id);
            } else {
                setMessage(res?.data?.message || 'Could not load the project');
            }
            setLoading(false);
        });
    }, [dispatch, isNew, projectId, loadFacilities]);

    useEffect(() => { load(); }, [load]);

    // ---------- draft updates ----------
    const touch = (fn) => { setDraft(d => fn(d)); setDirty(true); };
    const setProject = (field, value) => touch(d => ({ ...d, project: { ...d.project, [field]: value } }));
    const onClientChange = (clientId) => { touch(d => ({ ...d, project: { ...d.project, client_id: clientId, facility_id: '' } })); loadFacilities(clientId); };
    const updateItem = (key, patch) => touch(d => ({ ...d, items: d.items.map(i => (i.key === key ? { ...i, ...patch } : i)) }));
    const moveItem = (from, to) => touch(d => {
        if (to < 0 || to >= d.items.length) return d;
        const items = [...d.items]; const [m] = items.splice(from, 1); items.splice(to, 0, m);
        return { ...d, items };
    });
    const removeItem = (key) => {
        const it = draft.items.find(i => i.key === key);
        if (it?.kind === 'task' && it.task_id && !window.confirm(`Delete the task “${it.task_name}” and its ${it.sub_tasks.length} sub-task(s)? Site visits already recorded are kept.`)) return;
        if (it?.kind === 'task') it.sub_tasks.forEach(s => s.attachments.forEach(a => { if (a.id) setRemovedFiles(r => [...r, a.id]); }));
        touch(d => ({ ...d, items: d.items.filter(i => i.key !== key) }));
    };
    // "＋ Sub-tasks" on a simple step: it becomes a task with one empty sub-task
    const makeTask = (key) => {
        const t = { ...emptyTask(), key };
        touch(d => ({ ...d, items: d.items.map(i => (i.key === key ? { ...t, task_name: i.label, client_visible: i.client_visible, sub_tasks: [emptySubTask()] } : i)) }));
        setPanel({ taskKey: key, focusKey: null });
    };
    const addItem = (kind) => {
        const it = kind === 'task' ? emptyTask() : emptyStep();
        touch(d => ({ ...d, items: [...d.items, it] }));
        if (kind === 'task') setPanel({ taskKey: it.key, focusKey: null });
    };
    const updateTask = (taskKey, fn) => touch(d => ({ ...d, items: d.items.map(i => (i.key === taskKey ? fn(i) : i)) }));
    const openTask = (taskKey, subKey) => {
        if (subKey === 'new') {
            const s = emptySubTask();
            updateTask(taskKey, t => ({ ...t, sub_tasks: [...t.sub_tasks, s] }));
            setPanel({ taskKey, focusKey: s.key });
        } else setPanel({ taskKey, focusKey: subKey });
    };

    // ---------- save ----------
    const save = async () => {
        const { errors: errs, message: msg } = validateDraft(draft);
        setErrors(errs);
        if (msg) { setMessage(msg); return; }
        setSaving(true); setMessage(null);
        const p = draft.project;
        const body = {
            project: { facility_id: p.facility_id, description: p.description, project_owner: p.project_owner, project_budget: p.project_budget === '' ? null : p.project_budget, project_type: p.project_type || null, contract_years: p.contract_years },
            items: toPayloadItems(draft.items),
            version: draft.version,
        };
        const res = await dispatch(isNew ? createProjectFromEditor(body) : saveProjectEditor(projectId, body));
        if (!res?.data?.success) {
            setSaving(false);
            setConflict(!!res?.data?.conflict);
            setMessage(res?.data?.message || 'Could not save the project');
            return;
        }
        // Files: the same upload / delete calls the Settings form uses, now that every sub-task has an id
        const saved = res.data.data;
        const idMap = saved.id_map || {};
        try {
            for (const attId of removedFiles) await dispatch(deleteFacilityProjectAttachment(attId));
            for (const it of draft.items) {
                if (it.kind !== 'task') continue;
                for (const s of it.sub_tasks) {
                    const subId = s.id || idMap[s.key];
                    for (const a of s.attachments.filter(x => x.isNew)) {
                        if (!subId) continue;
                        const fd = new FormData();
                        fd.append('file', a.file);
                        fd.append('stage', 'attachment');
                        await dispatch(uploadFacilityProjectAttachment(subId, fd));
                    }
                }
            }
        } catch {
            setSaving(false);
            setMessage('The project was saved, but some files could not be uploaded. Open the project again to retry.');
            onSaved(saved.project.id, 'Project saved · some files were not uploaded', true);
            return;
        }
        setSaving(false);
        setDirty(false);
        onSaved(saved.project.id, isNew ? 'Project created' : 'Project saved');
    };

    const close = () => {
        if (dirty && !window.confirm('You have changes that are not saved. Close without saving?')) return;
        onClose();
    };

    const panelTask = panel && draft?.items.find(i => i.key === panel.taskKey);

    return (
        <Dialog open onClose={close} fullWidth maxWidth="lg" fullScreen={fullScreen} PaperProps={{ sx: { height: fullScreen ? '100%' : '92vh', borderRadius: fullScreen ? 0 : 4, bgcolor: '#f6f8f9' } }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e5e9ee' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>{isNew ? '＋ New project' : '✏️ Edit project'}</Typography>
                        {!isNew && draft && <Typography noWrap sx={{ fontSize: '12.5px', color: '#6b7785' }}>{draft.project.facility_name}{draft.project.description ? ` · ${draft.project.description}` : ''}</Typography>}
                    </Box>
                    <IconButton size="small" onClick={close} aria-label="Close"><CloseIcon /></IconButton>
                </Box>

                {loading || !draft ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        {message ? <Alert severity="error">{message}</Alert> : <CircularProgress sx={{ color: THEME_COLOR }} />}
                    </Box>
                ) : (
                    <Box sx={{ flex: 1, overflow: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 270px' }, gap: 2, p: 2, alignItems: 'start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <ProjectDetailsSection project={draft.project} isNew={isNew} clients={clients} facilities={facilities} greenUsers={greenUsers}
                                errors={errors} onChange={setProject} onClientChange={onClientChange} />
                            <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 3, p: 2, bgcolor: '#fff' }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '14.5px' }}>Timeline &amp; tasks</Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>Order here = order on the timeline. Tasks update themselves from their sub-tasks.</Typography>
                                </Box>
                                {draft.suggested && (
                                    <Alert severity="info" sx={{ mb: 1.5 }}>
                                        Standard steps (onboarding, kick-offs, invoicing…) were added around this project's tasks. Remove any you don't need. Nothing is saved until you press Save project.
                                    </Alert>
                                )}
                                <TimelineItemsList items={draft.items} errors={errors} taskTags={taskTags}
                                    onChange={updateItem} onMove={moveItem} onRemove={removeItem} onMakeTask={makeTask} onOpenTask={openTask} />
                                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                    <Button variant="outlined" onClick={() => addItem('task')} sx={buttonStyles.outlined}>＋ Task with sub-tasks</Button>
                                    <Button variant="outlined" onClick={() => addItem('step')} sx={buttonStyles.outlined}>＋ Simple step</Button>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, position: { md: 'sticky' }, top: 0 }}>
                            <ClientPreview items={draft.items} />
                            {!isNew && (
                                <BudgetVersionList projectId={projectId}
                                    facts={{ facility_name: draft.project.facility_name, project_type: draft.project.project_type, contract_years: draft.project.contract_years }} />
                            )}
                            <Box sx={{ fontSize: '12.5px', color: '#46525e', bgcolor: '#e9f1ff', borderRadius: 2.5, p: 1.5 }}>
                                📅 <b>{draft.items.reduce((n, i) => n + (i.kind === 'task' ? i.sub_tasks.filter(s => s.start_date).length : 0), 0)}</b> sub-tasks will show on the team calendar.
                                The Settings › Project Management form shows the same tasks and sub-tasks.
                            </Box>
                        </Box>
                    </Box>
                )}

                {draft && !loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.5, bgcolor: '#fff', borderTop: '1px solid #e5e9ee', flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            {message
                                ? <Alert severity={conflict ? 'warning' : 'error'} sx={{ py: 0 }} action={conflict ? <Button color="inherit" size="small" onClick={load}>Reload</Button> : null}>{message}</Alert>
                                : <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>{draft.items.length} steps · {draft.items.filter(i => i.client_visible).length} visible to the client</Typography>}
                        </Box>
                        <Button variant="outlined" onClick={close} disabled={saving} sx={buttonStyles.outlined}>Cancel</Button>
                        <Button variant="contained" onClick={save} disabled={saving} sx={buttonStyles.contained}>
                            {saving ? <CircularProgress size={20} color="inherit" /> : isNew ? 'Create project' : 'Save project'}
                        </Button>
                    </Box>
                )}

                {panelTask && (
                    <TaskPanel task={panelTask} errors={errors} focusKey={panel.focusKey} taskTags={taskTags} greenUsers={greenUsers}
                        onChange={(p) => updateItem(panelTask.key, p)}
                        onSubChange={(subKey, p) => updateTask(panelTask.key, t => ({ ...t, sub_tasks: t.sub_tasks.map(s => (s.key === subKey ? { ...s, ...p } : s)) }))}
                        onAddSub={() => { const s = emptySubTask(); updateTask(panelTask.key, t => ({ ...t, sub_tasks: [...t.sub_tasks, s] })); setPanel({ taskKey: panelTask.key, focusKey: s.key }); }}
                        onRemoveSub={(subKey) => {
                            const s = panelTask.sub_tasks.find(x => x.key === subKey);
                            if (s?.id && !window.confirm(`Delete the sub-task “${s.sub_task_name}”? Site visits already recorded are kept.`)) return;
                            s?.attachments.forEach(a => { if (a.id) setRemovedFiles(r => [...r, a.id]); });
                            updateTask(panelTask.key, t => ({ ...t, sub_tasks: t.sub_tasks.filter(x => x.key !== subKey) }));
                        }}
                        onAddFiles={(subKey, files) => updateTask(panelTask.key, t => ({ ...t, sub_tasks: t.sub_tasks.map(s => (s.key === subKey ? { ...s, attachments: [...s.attachments, ...files.map(f => ({ file: f, file_name: f.name, file_type: f.type, preview: URL.createObjectURL(f), isNew: true }))] } : s)) }))}
                        onRemoveFile={(subKey, idx) => {
                            const a = panelTask.sub_tasks.find(s => s.key === subKey)?.attachments[idx];
                            if (a?.id) setRemovedFiles(r => [...r, a.id]);
                            updateTask(panelTask.key, t => ({ ...t, sub_tasks: t.sub_tasks.map(s => (s.key === subKey ? { ...s, attachments: s.attachments.filter((_, i) => i !== idx) } : s)) }));
                        }}
                        onClose={() => setPanel(null)} />
                )}
            </LocalizationProvider>
        </Dialog>
    );
}
